import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';

import { getEventListThunk } from '../../../redux/Event/Event.thunk';

import { getEventList, resetEventList } from '../../../redux/Event/Event.slice';
import { setHeaderTitle } from '../../../redux/App/App.slice';

import {
  getDateTime,
  getDaysInMonthDisplay,
  isCurrentMonth,
} from '../../../services/date.service';

import ButtonComponent from '../../../components/Button.component';
import CardComponent from '../../../components/Card.component';
import EventsHeaderComponent from '../../../components/Events/EventsHeader.component';
import EventsPickerComponent from '../../../components/Events/EventsPicker.component';

import { DATE_YEAR_MONTH_DAY_FORMAT } from '../../../constants/date.contant';
import { Notification } from '../../../redux/Notification/NotificationType';
import {
  getNotificationCounterThunk,
  markAsReadNotificationThunk,
} from '../../../redux/Notification/NotificationThunk';
import useAuthHook from '../../../hooks/useAuth.hook';
import { getUserId, getUserRoles } from '../../../redux/User/UserSlice';
import { UserRole } from '../../../types/userRole.types';
import { StyledEventsPage, StyledTopBarPage } from './Events.styles';

const EventsPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const events = useAppSelector(getEventList);
  const userRoles = useAppSelector(getUserRoles);
  const userId = useAppSelector(getUserId);
  const [date, setDate] = useState(Date.now());
  const [disableTodayBtn, setDisableTodayBtn] = useState(true);
  const rangeDates = getDaysInMonthDisplay(date);
  const { user } = useAuthHook();

  const visibleEvents = useMemo(() => {
    const visibleAllRoles = [
      UserRole.SUPER_HR,
      UserRole.DEV_SUPER_HR,
      UserRole.DEV_ADMIN,
      UserRole.ADMIN,
    ];

    if (!userRoles) return [];

    if (userRoles.some(role => visibleAllRoles.includes(role))) {
      return events;
    }

    return events.filter(event => {
      return event.user?.id === userId;
    });
  }, [events, userId, userRoles]);

  useEffect(() => {
    dispatch(setHeaderTitle('Events'));
  }, [dispatch]);

  useEffect(() => {
    handleGetEventList();
    setDisableTodayBtn(isCurrentMonth(new Date(date)));
  }, [dispatch, date]);

  const handleGetEventList = (): Function => {
    const firstDate = rangeDates[0];
    const lastDate = rangeDates[rangeDates.length - 1];
    dispatch(
      getEventListThunk(
        `${getDateTime(
          firstDate.date,
          DATE_YEAR_MONTH_DAY_FORMAT,
        )},${getDateTime(lastDate.date, DATE_YEAR_MONTH_DAY_FORMAT)}`,
      ),
    );
    return () => {
      dispatch(resetEventList());
    };
  };

  const handleEventRead = (event: Notification) => {
    if (!event.readAt && user.id === event.user?.id) {
      dispatch(markAsReadNotificationThunk(event.uuid)).then(() => {
        dispatch(getNotificationCounterThunk());
      });
    }
  };

  return (
    <StyledEventsPage>
      <StyledTopBarPage>
        <ButtonComponent
          appearance="primary"
          disabled={disableTodayBtn}
          handleClick={() => setDate(Date.now())}
        >
          {t('Today')}
        </ButtonComponent>
        <EventsHeaderComponent
          date={date}
          onChangeMonth={(newDate: number) => setDate(newDate)}
        />
      </StyledTopBarPage>
      <CardComponent>
        <EventsPickerComponent
          onRead={handleEventRead}
          selectedDate={new Date(date)}
          notifications={visibleEvents}
          onUpdate={handleGetEventList}
          rangeDates={rangeDates}
        />
      </CardComponent>
    </StyledEventsPage>
  );
};

export default EventsPage;
